import React, { useState, useCallback } from 'react';

/**
 * EditableDataTable - FR-079
 * Supports inline editing of headers and cells, add/delete rows, add/delete columns.
 * Props:
 *   columns: [{ key, label }]
 *   data: [{ id, ...fields }]
 *   onDataChange: (updatedData) => void
 *   onColumnsChange: (updatedColumns) => void
 *   editable: boolean (controlled by Edit Mode toggle)
 *   renderCell: (row, colKey) => ReactNode (optional custom cell renderer)
 */
export default function EditableDataTable({
  columns: initialColumns,
  data: initialData,
  onDataChange,
  onColumnsChange,
  editable = false,
  renderCell,
  className = '',
}) {
  const [columns, setColumns] = useState(initialColumns);
  const [data, setData] = useState(initialData);
  const [editingCell, setEditingCell] = useState(null); // { rowIdx, colKey }
  const [editingHeader, setEditingHeader] = useState(null); // colKey

  // Sync when props change
  React.useEffect(() => { setData(initialData); }, [initialData]);
  React.useEffect(() => { setColumns(initialColumns); }, [initialColumns]);

  const updateCell = useCallback((rowIdx, colKey, value) => {
    const updated = data.map((row, i) => i === rowIdx ? { ...row, [colKey]: value } : row);
    setData(updated);
    onDataChange?.(updated);
  }, [data, onDataChange]);

  const updateHeader = useCallback((colIdx, newLabel) => {
    const updated = columns.map((col, i) => i === colIdx ? { ...col, label: newLabel } : col);
    setColumns(updated);
    onColumnsChange?.(updated);
  }, [columns, onColumnsChange]);

  const addRow = useCallback(() => {
    const newRow = { id: `new-${Date.now()}` };
    columns.forEach(col => { newRow[col.key] = ''; });
    const updated = [...data, newRow];
    setData(updated);
    onDataChange?.(updated);
  }, [data, columns, onDataChange]);

  const deleteRow = useCallback((rowIdx) => {
    if (!window.confirm('Delete this row?')) return;
    const updated = data.filter((_, i) => i !== rowIdx);
    setData(updated);
    onDataChange?.(updated);
  }, [data, onDataChange]);

  const addColumn = useCallback((afterIdx) => {
    const newKey = `col_${Date.now()}`;
    const newCol = { key: newKey, label: 'New Column' };
    const updated = [...columns];
    updated.splice(afterIdx + 1, 0, newCol);
    setColumns(updated);
    onColumnsChange?.(updated);
    // Add empty field to all rows
    const updatedData = data.map(row => ({ ...row, [newKey]: '' }));
    setData(updatedData);
    onDataChange?.(updatedData);
  }, [columns, data, onColumnsChange, onDataChange]);

  const deleteColumn = useCallback((colIdx) => {
    if (columns.length <= 1) return;
    if (!window.confirm('Delete this column? All data in it will be lost.')) return;
    const colKey = columns[colIdx].key;
    const updatedCols = columns.filter((_, i) => i !== colIdx);
    setColumns(updatedCols);
    onColumnsChange?.(updatedCols);
    const updatedData = data.map(row => {
      const newRow = { ...row };
      delete newRow[colKey];
      return newRow;
    });
    setData(updatedData);
    onDataChange?.(updatedData);
  }, [columns, data, onColumnsChange, onDataChange]);

  const handleCellClick = (rowIdx, colKey) => {
    if (editable) setEditingCell({ rowIdx, colKey });
  };

  const handleCellBlur = () => { setEditingCell(null); };

  const handleHeaderClick = (colKey) => {
    if (editable) setEditingHeader(colKey);
  };

  return (
    <div>
      {editable && (
        <div style={{
          display:'flex', alignItems:'center', gap:14, marginBottom:10, padding:'7px 14px',
          background:'linear-gradient(90deg, #2563EB, #1046A0)', borderRadius:8,
          color:'#fff', fontSize:12, fontWeight:600
        }}>
          <span>✏️ Edit Mode ON</span>
          <span style={{opacity:.7,fontWeight:400}}>Click any cell to edit. Changes are tracked.</span>
        </div>
      )}
      <div className="table-wrap">
        <table className={className}>
          <thead>
            <tr>
              {columns.map((col, ci) => (
                <th key={col.key} style={{ position:'relative' }}>
                  {editable && editingHeader === col.key ? (
                    <input
                      type="text"
                      value={col.label}
                      onChange={e => updateHeader(ci, e.target.value)}
                      onBlur={() => setEditingHeader(null)}
                      autoFocus
                      style={{ fontSize:11, padding:'2px 6px', border:'1px solid #2563EB', borderRadius:4, width:'100%', background:'#fff', fontWeight:600 }}
                    />
                  ) : (
                    <span
                      onClick={() => handleHeaderClick(col.key)}
                      style={{ cursor: editable ? 'text' : 'default' }}
                    >
                      {col.label}
                    </span>
                  )}
                  {editable && (
                    <span style={{display:'inline-flex',gap:3,marginLeft:6,verticalAlign:'middle'}}>
                      <span
                        title="Insert column right"
                        onClick={(e) => {e.stopPropagation(); addColumn(ci);}}
                        style={{cursor:'pointer',width:16,height:16,borderRadius:3,background:'#EFF6FF',color:'#1046A0',fontSize:10,fontWeight:700,display:'inline-flex',alignItems:'center',justifyContent:'center',border:'1px solid #BFDBFE'}}
                      >+</span>
                      <span
                        title="Delete column"
                        onClick={(e) => {e.stopPropagation(); deleteColumn(ci);}}
                        style={{cursor:'pointer',width:16,height:16,borderRadius:3,background:'#FEF2F2',color:'#DC2626',fontSize:10,fontWeight:700,display:'inline-flex',alignItems:'center',justifyContent:'center',border:'1px solid #FECACA'}}
                      >&times;</span>
                    </span>
                  )}
                </th>
              ))}
              {editable && <th style={{width:30}}></th>}
            </tr>
          </thead>
          <tbody>
            {data.map((row, ri) => (
              <tr key={row.id || ri}>
                {columns.map(col => (
                  <td
                    key={col.key}
                    onClick={() => handleCellClick(ri, col.key)}
                    style={{
                      cursor: editable ? 'text' : 'default',
                      background: editingCell?.rowIdx === ri && editingCell?.colKey === col.key ? 'rgba(37,99,235,.06)' : undefined,
                      boxShadow: editingCell?.rowIdx === ri && editingCell?.colKey === col.key ? '0 0 0 2px #2563EB' : undefined,
                      borderRadius: editingCell?.rowIdx === ri && editingCell?.colKey === col.key ? 4 : undefined,
                    }}
                  >
                    {editable && editingCell?.rowIdx === ri && editingCell?.colKey === col.key ? (
                      <input
                        type="text"
                        value={row[col.key] ?? ''}
                        onChange={e => updateCell(ri, col.key, e.target.value)}
                        onBlur={handleCellBlur}
                        autoFocus
                        style={{ fontSize:13, padding:'2px 6px', border:'1px solid #2563EB', borderRadius:4, width:'100%', background:'#fff' }}
                      />
                    ) : (
                      renderCell ? renderCell(row, col.key) : (row[col.key] ?? '')
                    )}
                  </td>
                ))}
                {editable && (
                  <td style={{width:30,textAlign:'center'}}>
                    <span
                      onClick={() => deleteRow(ri)}
                      title="Delete row"
                      style={{cursor:'pointer',width:18,height:18,background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:4,color:'#DC2626',fontSize:11,display:'inline-flex',alignItems:'center',justifyContent:'center',fontWeight:700}}
                    >&times;</span>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editable && (
        <div
          onClick={addRow}
          style={{marginTop:8,fontSize:12,color:'#2563EB',cursor:'pointer',padding:'6px 10px',border:'1px dashed #2563EB',borderRadius:6,background:'#EFF6FF',fontWeight:600,width:'100%',textAlign:'center'}}
        >
          + Add Row
        </div>
      )}
    </div>
  );
}
